import { Select } from "antd";
import React, { SyntheticEvent, useEffect, useState } from "react";
import { SelectionMode } from "@models/index";
import supplierFetcherHoc from "@hocs/supplierFetcherHoc";
import { Supplier } from "@models/supplier";

const Option = Select.Option;

export interface SupplierMultiSelectionReturnValue {
  new_suppliers: string[];
  deleted_suppliers: string[];
  selectedItems: Supplier[];
}

export const supplierMultiSelectionDefaultReturnValue: SupplierMultiSelectionReeturnValue = {
  new_suppliers: [],
  deleted_suppliers: [],
  selectedItems: []
};

type SupplierSelectionProps = {
  mode: SelectionMode;
  onChange: (value: SupplierMultiSelectionReturnValue) => void;
  selectedSuppliers: Supplier[];
  data: Supplier[];
  onScroll: (e: SyntheticEvent) => void;
  handleSearch: (query: string) => void;
  fetchData: (params: object | null) => void;
  filter?: object;
  isFetched: boolean;
  initialValue: Supplier[] | Supplier | null;
};

/**
 * This class handle selection of suppliers
 * Use Select fro ant-design because it has on scroll option
 * In terms of lazy loading of data, selected items should bre rendered separated
 * On Change callback return an object from new and deleted suppliers
 * @param props
 * @constructor
 */
const SuppliersSelection: React.FC<SupplierSelectionProps> = props => {
  const {
    handleSearch,
    onScroll,
    data = [],
    initialValue,
    onChange,
    fetchData,
    isFetched,
    mode = SelectionMode.DEFAULT
  } = props;
  const defaultValue = mode == SelectionMode.MULTIPLE ? [] : null;

  const retrieveValueFromProps = () => {
    if (mode == SelectionMode.MULTIPLE) {
      const returnValue = props.initialValue || [];
      return returnValue.map(item => item.id);
    } else {
      return props.initialValue ? props.initialValue.id : null;
    }
  };

  const [value, setValue] = useState(
    initialValue ? retrieveValueFromProps() : defaultValue
  );

  useEffect(() => {
    const value = retrieveValueFromProps();
    setValue(value);
  }, [props.initialValue]);

  /**
   * To handle show selected suppliers before fetch data
   */
  const getData = () => {
    if (mode == SelectionMode.MULTIPLE) {
      let returnData = data;
      const selectedValues = props.initialValue || [];
      selectedValues.forEach(supplier => {
        if (returnData.findIndex(opt => opt.id === supplier.id) === -1) {
          returnData.push(supplier);
        }
      });

      return returnData;
    } else {
      return initialValue && !data.find(opt => opt.id == initialValue.id)
        ? [{ name: "هیچ کدام", id: null }, ...data, initialValue]
        : [{ name: "هیچ کدام", id: null }, ...data];
    }
  };

  const onChangeValue = (newValue, option) => {
    setValue(newValue);
    if (mode == SelectionMode.MULTIPLE) {
      const selectedValues = props.initialValue || [];
      const oldValue = selectedValues.map(cat => cat.id);
      const deleted_suppliers = oldValue.filter(
        item => newValue.findIndex(id => id === item) === -1
      );
      const new_suppliers = newValue.filter(
        item => oldValue.findIndex(id => id === item) === -1
      );

      props.onChange({
        deleted_suppliers,
        new_suppliers,
        selectedItems: option.map(opt => opt.props.supplier)
      });
    } else {
      onChange(option.props.supplier);
    }
  };

  const onFocus = () => {
    if (isFetched == false) {
      fetchData({});
    }
  };

  return (
    <Select
      showSearch
      mode={mode}
      value={value}
      defaultActiveFirstOption={false}
      showArrow={true}
      filterOption={false}
      onChange={onChangeValue}
      onFocus={onFocus}
      onSearch={handleSearch}
      onPopupScroll={onScroll}
      notFoundContent={null}
    >
      {getData().map(supplier => (
        <Option key={supplier.id} supplier={supplier}>
          {supplier.name}
        </Option>
      ))}
    </Select>
  );
};

export default supplierFetcherHoc(SuppliersSelection);
